import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { Grid, Button, ButtonGroup, Box } from "@mui/material";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import moment from "moment-timezone";
import { ICrewEvent } from "src/redux/reducers/CrewScheduleReducer";
import { ICrewsNonFlyingAssignment, INonFlyingType } from "@vmosolutions/oc-interfaces";
import { NonFlightChip } from "src/components/Pairings/NonFlightChip";
import { convertToPlannedNFA } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { Formik, FormikProps, Form } from "formik";
import { FormikSelectField } from "src/components/MUI5FormikComponents/FormikWrappers";
import { removeNFAFromCrew } from "src/redux/actions/CrewSchedule/nonFlyingAssignments";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { debounce } from "lodash";
import { openWindow } from "src/redux/actions/Mosaic.Actions";

interface ITimelineAddEvents {
  id: string;
  path: string[];
}

type INonFlyingAssignmentForm = FormikProps<ICrewsNonFlyingAssignment>;

const initialValues: ICrewsNonFlyingAssignment = {
  _id: undefined,
  start: undefined,
  end: undefined,
  locationICAO: undefined,
  locationTimezoneId: undefined,
  locationCountry: undefined,
  assignmentType: undefined,
  crew: [],
  createdAt: undefined,
  createdBy: undefined,
  foreignRef: undefined,
};

/** Sort types list Alphabetically */
export const sortTypesByDescription = (a: INonFlyingType, b: INonFlyingType): number => {
  return b.description > a.description ? -1 : 1;
};

/**
 * CREW SCHEDULE - Manage Non Flying Assignments
 * Displays a series of quicklinks to the flight builder component so that users can jump straight to the type of timeline event builder they want.
 */
export const ManageNFAs = (props: ITimelineAddEvents): JSX.Element => {
  const dispatch = useDispatch();

  // Selected Crew Days
  const selectedCrewDays = useSelector((state: IRootState) => state.crewSchedule.selectedCrewDays);
  const crewNonFlyingAssignments = useSelector((state: IRootState) => state.crewSchedule.crewNonFlyingAssignments);

  // Non Flying Assignments
  const nonFlyingTypes = useSelector((state: IRootState) => state.structures.customerStructure.nonFlyingTypes);

  const nonFlyingTypesOptions = useMemo(
    () => nonFlyingTypes.filter((nonFlyingType: INonFlyingType) => nonFlyingType.enabled).sort(sortTypesByDescription),
    [nonFlyingTypes]
  );

  const [crewEvents, setCrewEvents] = useState<ICrewEvent[]>([]);
  const [selectedCrewEvent, setSelectedCrewEvent] = useState<ICrewEvent>(null);
  const crewMemberOnSelectedNFA = selectedCrewEvent?.crewId;
  const [initialFormValues, setInitialFormValues] = useState<ICrewsNonFlyingAssignment>(initialValues);
  const [doUpdateFormValues, setDoUpdateFormValues] = useState<boolean>(true);

  useEffect(() => {
    setSelectedCrewEvent(null);
  }, [selectedCrewDays]);

  const selectedNFA = useMemo(() => {
    const type = nonFlyingTypesOptions?.find(
      (type) => type.title === (selectedCrewEvent?.event as ICrewsNonFlyingAssignment)?.assignmentType?.title
    );
    return selectedCrewEvent?.event
      ? ({ ...selectedCrewEvent?.event, assignmentType: type } as ICrewsNonFlyingAssignment)
      : null;
  }, [selectedCrewEvent, nonFlyingTypesOptions]);

  useEffect(() => {
    if (doUpdateFormValues) {
      if (selectedNFA?._id) {
        setInitialFormValues(selectedNFA);
      } else {
        setInitialFormValues(initialValues);
      }
    }
  }, [selectedNFA, doUpdateFormValues]);

  useEffect(() => {
    if (!selectedCrewDays || !crewNonFlyingAssignments) return;
    const { crewId, selectedTimes } = selectedCrewDays;
    if (!selectedTimes) return;

    const NFAs = crewNonFlyingAssignments[crewId]?.nonFlyingAssignments
      ? crewNonFlyingAssignments[crewId]?.nonFlyingAssignments
      : [];
    const assigned = NFAs.filter((NFA) => {
      for (let i = 0; i < selectedTimes.length; i++) {
        const startOf = moment.utc(selectedTimes[i]).startOf("day")?.valueOf();
        const endOf = moment.utc(selectedTimes[i]).endOf("day")?.valueOf();
        if ((NFA.start as number) > startOf && (NFA.start as number) < endOf) {
          return true;
        }
      }
      return false;
    });

    const crewEvents: ICrewEvent[] =
      assigned?.length > 0
        ? assigned.map((nfa) => ({
            event: nfa,
            crewId: crewId,
            isPlanned: false,
          }))
        : [];

    setCrewEvents(crewEvents);
  }, [selectedCrewDays, crewNonFlyingAssignments, nonFlyingTypes]);

  // Handle assignment of none flying assignments to selected crew/days.
  const handleUpdateNFA = useCallback(
    async (nonFlyingAssignment: ICrewsNonFlyingAssignment): Promise<void> => {
      const mode = nonFlyingAssignment?._id ? "update" : "create";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Adding"} NFA on selected crew.`,
        successMessage: `NFA successful ${mode === "update" ? "Updated" : "Added"}`,
        errorMessage: `Error: Failed to ${mode === "update" ? "Update" : "Add"} NFA`,
      };
      if (mode === "update") {
        const response = await AmplifyAPIWrapper.Put<ICrewsNonFlyingAssignment>(
          `/nfas/updateNFA/${nonFlyingAssignment._id}`,
          { assignmentType: nonFlyingAssignment.assignmentType },
          notifications
        );
        if (Array.isArray(response) && response[0]?._id) {
          setSelectedCrewEvent(response[0] as ICrewEvent);
        } else {
          if (response?._id) setInitialFormValues(response);
        }
      } else {
        // ADD NEW
        const body = {
          crewId: selectedCrewDays?.crewId,
          nonFlyingTypeId: nonFlyingAssignment?.assignmentType?._id,
          selectedTimes: selectedCrewDays.selectedTimes,
        };
        const response = await AmplifyAPIWrapper.Post<ICrewsNonFlyingAssignment>("/nfas", body, notifications);
        if (Array.isArray(response) && response[0]?._id) {
          setSelectedCrewEvent(response[0] as ICrewEvent);
        }
      }
    },
    [dispatch, selectedCrewDays]
  );

  const convertToPlanned = useCallback(
    async (paintNFA: INonFlyingAssignmentForm): Promise<void> => {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "info",
          message: "Converting NFA to planned event",
        })
      );
      const response = await convertToPlannedNFA(selectedNFA?._id, [crewMemberOnSelectedNFA]);
      if (Array.isArray(response) && response[0]?._id) {
        setSelectedCrewEvent(null);
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "NFA Converted to planned successfully.",
          })
        );
      } else {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "error",
            message: "Unable to convert NFA, please see administrator for assistance.",
          })
        );
      }
      paintNFA?.resetForm({ values: initialValues });
    },
    [crewMemberOnSelectedNFA, dispatch, selectedNFA]
  );

  // Remove NFA
  const removeAssignment = useCallback(
    async (paintNFA: INonFlyingAssignmentForm): Promise<void> => {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "info",
          message: "Removing NFA",
        })
      );
      await removeNFAFromCrew(selectedNFA?._id, crewMemberOnSelectedNFA);
      setSelectedCrewEvent(null);
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "success",
          message: "NFA Removed Successfully.",
        })
      );
      paintNFA?.resetForm({ values: initialValues });
    },
    [crewMemberOnSelectedNFA, dispatch, selectedNFA]
  );

  return (
    <Grid container spacing={0}>
      {crewEvents?.length > 0 && (
        <Grid
          item
          xs={12}
          sx={{
            justifyContent: "center",
            display: "flex",
          }}
        >
          {crewEvents.map((crewEvent: ICrewEvent, index: number) => {
            const nonFlying = crewEvent.event as ICrewsNonFlyingAssignment;
            const isSelected = crewEvent.event?._id === selectedNFA?._id;
            return (
              <Box
                key={index}
                onClick={() => {
                  setDoUpdateFormValues(true);
                  setSelectedCrewEvent(isSelected ? null : crewEvent);
                }}
                sx={(theme) => ({ border: isSelected ? `1px solid  ${theme.palette.text.primary}` : "" })}
              >
                <NonFlightChip data={nonFlying} />
              </Box>
            );
          })}
        </Grid>
      )}

      <Formik initialValues={initialFormValues} enableReinitialize={true} onSubmit={handleUpdateNFA}>
        {(paintNFA: INonFlyingAssignmentForm) => {
          return (
            <Grid item xs={12}>
              <Form>
                <Grid container spacing={0} sx={{ width: "100%" }}>
                  <Grid item xs={12}>
                    <FormikSelectField
                      name="assignmentType"
                      options={nonFlyingTypesOptions}
                      getOptionLabel={(type: INonFlyingType) => `${type.description} (${type.title})`}
                      getOptionValue={(type: INonFlyingType) => type._id}
                      isSearchable={true}
                      menuPosition={"fixed"}
                      openMenuOnFocus={false}
                      isClearable={true}
                      placeholder={`Select NFA to ${selectedNFA ? "switch to" : "add"}`}
                      onChange={(value) => {
                        if (value) {
                          setDoUpdateFormValues(false);
                          const findEvent = crewEvents.filter((e) => {
                            const nfa = e.event as ICrewsNonFlyingAssignment;
                            return nfa.assignmentType.title === value.title;
                          });
                          if (findEvent.length === 1) {
                            setDoUpdateFormValues(true);
                            setSelectedCrewEvent(findEvent[0]);
                          } else {
                            setSelectedCrewEvent(null);
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonGroup size="small" variant="contained" fullWidth>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="small"
                        color="success"
                        disabled={
                          !paintNFA.dirty ||
                          !paintNFA?.values?.assignmentType?._id ||
                          paintNFA?.values?.assignmentType?._id === selectedNFA?._id
                        }
                        sx={{ fontSize: "0.7em", borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                      >
                        {!selectedNFA ? "Add" : "Switch"}
                      </Button>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="small"
                        color="info"
                        disabled={
                          (!paintNFA.dirty && !selectedNFA) ||
                          paintNFA?.values?.assignmentType?._id !== selectedNFA?.assignmentType?._id
                        }
                        sx={{ fontSize: "0.7em", borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                        onClick={() => {
                          dispatch(
                            openWindow(
                              "Edit Non-Flying Assignment",
                              "NFAEditorWindow",
                              props.path,
                              { nfaId: selectedNFA?._id },
                              "context"
                            )
                          );
                        }}
                      >
                        Edit
                      </Button>
                    </ButtonGroup>
                  </Grid>
                  {selectedNFA && (
                    <Grid item xs={12}>
                      <ButtonGroup size="small" variant="contained" fullWidth>
                        <Button
                          color="primary"
                          sx={{ fontSize: "0.7em", whiteSpace: "nowrap", width: "70%", borderTopLeftRadius: 0 }}
                          onClick={debounce(() => convertToPlanned(paintNFA), 300)}
                        >
                          Convert To Planned Event
                        </Button>

                        <Button
                          color="warning"
                          sx={{ fontSize: "0.7em", width: "30%", borderTopRightRadius: 0 }}
                          onClick={debounce(() => removeAssignment(paintNFA), 300)}
                        >
                          Unassign
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  )}
                </Grid>
              </Form>
            </Grid>
          );
        }}
      </Formik>
    </Grid>
  );
};
